import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { User, LogOut, Calendar, Settings, Clock, LogIn, KeyRound, Gift, Mail, Menu as MenuIcon, X, ChevronDown, DollarSign, Home as HomeIcon, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { EmployeeMessagesBell } from './EmployeeMessagesBell';
import { useCmsContent } from '../hooks/useCmsContent';
import { useMenuConfig } from '../hooks/useMenuConfig';
import { resolveFontFamily } from '../lib/fonts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------- Employee PIN login widget (visible when not logged in) ----------
const EmployeePinWidget = ({ onLoginSuccess }) => {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDigit = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(v);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pin.length !== 4) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/auth/pin-login`,
        { pin },
        { withCredentials: true }
      );
      toast.success(`Welcome, ${res.data.name}`);
      setPin('');
      onLoginSuccess?.(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid PIN';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="hidden md:flex items-center gap-2 mr-2"
      data-testid="employee-pin-widget"
    >
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E8E5DF] bg-white">
        <KeyRound className="w-4 h-4 text-[#6B6B7A]" />
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={handleDigit}
          placeholder="Employee PIN"
          maxLength={4}
          className="w-24 text-sm bg-transparent outline-none font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
          aria-label="Employee 4-digit PIN"
          data-testid="header-pin-input"
        />
      </div>
      <Button
        type="submit"
        size="sm"
        disabled={loading || pin.length !== 4}
        className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full px-3 py-1.5 text-xs"
        data-testid="header-pin-login-btn"
      >
        <LogIn className="w-3.5 h-3.5 mr-1" />
        {loading ? '...' : 'Sign In'}
      </Button>
    </form>
  );
};

// ---------- Quick Time In/Out buttons (visible when employee is logged in) ----------
const EmployeeQuickActions = () => {
  const [status, setStatus] = useState({ clocked_in: false });
  const [loading, setLoading] = useState(false);
  const pollingRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employee/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      /* swallow */
    }
  };

  useEffect(() => {
    fetchStatus();
    pollingRef.current = setInterval(fetchStatus, 30000);
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, []);

  const clockIn = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/employee/clock-in`, {}, { withCredentials: true });
      toast.success('Clocked in');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const clockOut = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/employee/clock-out`, {}, { withCredentials: true });
      toast.success(`Clocked out (${res.data.hours || 0}h)`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mr-1 sm:mr-2" data-testid="header-quick-time">
      {!status.clocked_in ? (
        <Button
          size="sm"
          onClick={clockIn}
          disabled={loading}
          className="bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full px-2.5 sm:px-4 py-1.5 text-xs"
          data-testid="header-time-in-btn"
        >
          <Clock className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Time In</span>
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={clockOut}
          disabled={loading}
          className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full px-2.5 sm:px-4 py-1.5 text-xs"
          data-testid="header-time-out-btn"
        >
          <Clock className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Time Out</span>
        </Button>
      )}
    </div>
  );
};

export const Header = () => {
  const { user, logout, isAdmin, hasCrmAccess, checkAuth } = useAuth();
  const navigate = useNavigate();
  const isEmployee = user?.role === 'employee';
  const cms = useCmsContent();
  // iter-104: top-left brand text is now CMS-driven.
  const brand = cms?.brand || {};
  const brandText = brand.text || 'Creative Birthdays';
  const brandFontFamily = resolveFontFamily(brand.font_family, '"Fredoka", sans-serif');
  const brandFontSize = Number(brand.font_size_px) > 0 ? `${brand.font_size_px}px` : undefined;
  const brandColor = brand.color || '#FF3B6B';
  const brandWeight = Number(brand.weight) || 700;
  const brandLetterSpacing = brand.letter_spacing_px ? `${brand.letter_spacing_px}px` : undefined;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menu = useMenuConfig();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mstyle = menu.style || {};
  const navFont = resolveFontFamily(mstyle.font, '"Poppins", sans-serif');
  const sizeCls = { sm: 'text-sm', base: 'text-[15px]', lg: 'text-lg' }[mstyle.size] || 'text-[15px]';
  const linkStyle = { color: mstyle.text_color || '#6B6B7A', fontFamily: navFont, fontWeight: Number(mstyle.weight) || 500 };
  const alignCls = { left: 'justify-start', center: 'justify-center', right: 'justify-end' }[mstyle.align] || 'justify-end';
  const stickyCls = mstyle.sticky === false ? '' : 'sticky top-0';

  const goHash = (e, link) => {
    e.preventDefault();
    const id = (link.split('#')[1] || '').trim();
    if (window.location.pathname === '/' && id) {
      const el = document.getElementById(id);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    navigate(link);
  };

  const NavAnchor = ({ item, onNavigate }) => {
    const cls = `${sizeCls} transition-opacity font-medium hover:opacity-70`;
    const done = () => onNavigate && onNavigate();
    if (item.is_cta) {
      return (
        <Link to={item.link} onClick={done} data-testid={`nav-cta-${item.id}`}
          className={`inline-flex items-center rounded-full px-4 py-2 font-semibold ${sizeCls} hover:-translate-y-0.5 transition-transform`}
          style={{ background: item.cta_bg || '#FF3B6B', color: item.cta_color || '#FFFFFF', fontFamily: navFont }}>
          {item.label}
        </Link>
      );
    }
    const external = /^https?:\/\//.test(item.link || '');
    const isHash = (item.link || '').includes('#');
    if (external) return <a href={item.link} target={item.target || '_self'} rel="noreferrer" className={cls} style={linkStyle} onClick={done} data-testid={`nav-${item.id}`}>{item.label}</a>;
    if (isHash) return <a href={item.link} className={cls} style={linkStyle} onClick={(e) => { goHash(e, item.link); done(); }} data-testid={`nav-${item.id}`}>{item.label}</a>;
    return <Link to={item.link} target={item.target || '_self'} className={cls} style={linkStyle} onClick={done} data-testid={`nav-${item.id}`}>{item.label}</Link>;
  };

  const DesktopItem = ({ item }) => {
    if (item.children && item.children.length) {
      return (
        <div className="relative group" data-testid={`nav-group-${item.id}`}>
          <button className={`${sizeCls} font-medium inline-flex items-center gap-1 hover:opacity-70`} style={linkStyle}>{item.label}<ChevronDown className="w-3.5 h-3.5" /></button>
          <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
            <div className="bg-white rounded-xl shadow-lg border border-[#E8E5DF] p-1 min-w-[190px]">
              {item.children.map((c) => (
                <div key={c.id} className="px-3 py-2 rounded-lg hover:bg-[#FAF8F4]"><NavAnchor item={c} /></div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    return <NavAnchor item={item} />;
  };

  const brandLogo = mstyle.show_logo === false ? null : (
    <Link to="/" className="flex items-center gap-2 min-w-0 shrink" data-testid="header-logo">
      <span
        style={{
          fontFamily: brandFontFamily,
          ...(brandFontSize ? { fontSize: brandFontSize } : {}),
          color: brandColor,
          fontWeight: brandWeight,
          ...(brandLetterSpacing ? { letterSpacing: brandLetterSpacing } : {}),
        }}
        className={brandFontSize ? 'truncate' : 'text-xl sm:text-2xl font-bold truncate'}
        data-testid="header-brand-text"
      >
        {brandText}
      </span>
    </Link>
  );

  return (
    <header className={`glass-header ${stickyCls} z-50 w-full`} style={mstyle.bg_color ? { background: mstyle.bg_color } : undefined}>
      <div className="container mx-auto px-4 h-16 flex items-center gap-2 sm:gap-4">
        {brandLogo}

        <nav className={`hidden md:flex flex-1 items-center gap-6 ${alignCls}`} data-testid="main-nav">
          {isEmployee ? (
            <>
              <Link to="/employee" className={`${sizeCls} font-medium hover:opacity-70`} style={linkStyle} data-testid="nav-emp-home">Home</Link>
              {hasCrmAccess && (
                <Link to="/crm" className={`${sizeCls} font-medium hover:opacity-70`} style={linkStyle} data-testid="nav-crm">CRM</Link>
              )}
            </>
          ) : (
            <>
              {(menu.items || []).map((it) => <DesktopItem key={it.id} item={it} />)}
              {hasCrmAccess && (
                <Link to="/crm" className={`${sizeCls} font-medium hover:opacity-70`} style={linkStyle} data-testid="nav-crm">CRM</Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto md:ml-0">
          {isEmployee && <EmployeeQuickActions />}
          {isEmployee && <EmployeeMessagesBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-full border-[#E8E5DF] hover:border-[#FF3B6B] hover:text-[#FF3B6B] px-3"
                  data-testid="user-menu-trigger"
                >
                  <User className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{isEmployee ? `Welcome, ${user.name}` : user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {isEmployee ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/employee#profile')} data-testid="menu-emp-profile">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/employee#paystubs')} data-testid="menu-emp-paystubs">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Paystubs
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/my-bookings')} data-testid="menu-my-bookings">
                      <Calendar className="w-4 h-4 mr-2" />
                      My Bookings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-messages')} data-testid="menu-my-messages">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      My Messages
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-loyalty')} data-testid="menu-my-loyalty">
                      <Gift className="w-4 h-4 mr-2" />
                      My Loyalty
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-invites')} data-testid="menu-my-invites">
                      <Mail className="w-4 h-4 mr-2" />
                      My Invites
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-gift-cards')} data-testid="menu-my-gift-cards">
                      <Gift className="w-4 h-4 mr-2" />
                      My Gift Cards
                    </DropdownMenuItem>
                    {hasCrmAccess && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Settings className="w-4 h-4 mr-2" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    {hasCrmAccess && (
                      <DropdownMenuItem onClick={() => navigate('/crm')} data-testid="menu-crm">
                        <Settings className="w-4 h-4 mr-2" />
                        CRM
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="hidden sm:inline-flex text-[#6B6B7A] hover:text-[#1E1E24]"
              data-testid="header-login-btn"
            >
              Log in
            </Button>
          )}

          <button
            type="button"
            className="md:hidden p-2 rounded-lg border border-[#E8E5DF] text-[#1E1E24]"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[#E8E5DF] bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
          {isEmployee ? (
            <>
              <button onClick={() => { setMobileOpen(false); navigate('/employee'); }} className="flex items-center gap-2 py-2 text-[#1E1E24] font-medium w-full" data-testid="mobile-emp-home"><HomeIcon className="w-4 h-4" /> Home</button>
              {hasCrmAccess && (
                <button onClick={() => { setMobileOpen(false); navigate('/crm'); }} className="flex items-center gap-2 py-2 text-[#1E1E24] font-medium w-full" data-testid="mobile-emp-crm"><Settings className="w-4 h-4" /> CRM</button>
              )}
              <button onClick={() => { setMobileOpen(false); navigate('/employee#profile'); }} className="flex items-center gap-2 py-2 text-[#1E1E24] font-medium w-full" data-testid="mobile-emp-profile"><User className="w-4 h-4" /> Profile</button>
              <button onClick={() => { setMobileOpen(false); navigate('/employee#paystubs'); }} className="flex items-center gap-2 py-2 text-[#1E1E24] font-medium w-full" data-testid="mobile-emp-paystubs"><DollarSign className="w-4 h-4" /> Paystubs</button>
              <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="flex items-center gap-2 py-2 text-[#FF3B6B] font-medium w-full" data-testid="mobile-emp-logout"><LogOut className="w-4 h-4" /> Logout</button>
            </>
          ) : (
            <>
              {(menu.items || []).map((it) => (
                <div key={it.id}>
                  <div className="py-2"><NavAnchor item={it} onNavigate={() => setMobileOpen(false)} /></div>
                  {(it.children || []).map((c) => (
                    <div key={c.id} className="py-1.5 pl-4"><NavAnchor item={c} onNavigate={() => setMobileOpen(false)} /></div>
                  ))}
                </div>
              ))}
              {hasCrmAccess && (
                <button onClick={() => { setMobileOpen(false); navigate('/crm'); }} className="flex items-center gap-2 py-2 text-[#1E1E24] font-medium w-full" data-testid="mobile-crm"><Settings className="w-4 h-4" /> CRM</button>
              )}
              {!user && (
                <button onClick={() => { setMobileOpen(false); navigate('/login'); }} className="py-2 text-[#6B6B7A] font-medium" data-testid="mobile-login">Log in</button>
              )}
            </>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
